/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useRef, useEffect, useState, useMemo } from 'react';
import {
  dropTargetForElements,
  draggable,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Badge } from 'antd';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { arrToTree, TableEntry } from '~src/utils/securityGroupsConfig';

interface DroppableFolderListProps {
  existingFolders: DbFolder[];
  newFolders: any[];
  onDrop: (folder: DbFolder) => void;
  onNest: (draggedId: number, targetId: number) => void;
  onRemove: (folderId: number) => void;
  canNest?: (draggedId: number, targetId: number) => boolean;
}

export const DroppableFolderList: React.FC<DroppableFolderListProps> = ({
  existingFolders,
  newFolders,
  onDrop,
  onNest,
  onRemove,
  canNest,
}) => {
  const styles = useStyles(styleFn);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source }) => {
        setIsDraggedOver(false);
        const folderData = source.data.folder as DbFolder;
        const isNewFolder = source.data.isNew as boolean;
        if (folderData && !isNewFolder) {
          onDrop(folderData);
        }
      },
    });
  }, [onDrop]);

  const sortedExisting = useMemo(() => {
    return [...existingFolders].sort((a, b) => a.name.localeCompare(b.name));
  }, [existingFolders]);

  const existingTree = useMemo(() => {
    return arrToTree(sortedExisting, null);
  }, [sortedExisting]);

  const sortedNew = useMemo(() => {
    return [...newFolders].sort((a, b) => a.name.localeCompare(b.name));
  }, [newFolders]);

  const newTree = useMemo(() => {
    return arrToTree(sortedNew, null);
  }, [sortedNew]);

  const hasContent =
    existingTree.length > 0 || newTree.length > 0 || isDraggedOver;

  return (
    <div
      ref={containerRef}
      css={[styles.container, isDraggedOver && styles.containerDraggedOver]}>
      {existingTree.length > 0 && (
        <div css={styles.section}>
          {existingTree.map((folder) => (
            <ExistingFolderItem key={folder.key} folder={folder} level={0} />
          ))}
        </div>
      )}

      {newTree.length > 0 && (
        <div css={styles.section}>
          {newTree.map((folder) => (
            <DroppableFolderItem
              key={folder.key}
              folder={folder}
              level={0}
              onNest={onNest}
              onRemove={onRemove}
              canNest={canNest}
            />
          ))}
        </div>
      )}

      {!hasContent && (
        <div css={styles.emptyState}>
          <FontAwesomeIcon
            icon={['fad', 'folder-plus']}
            css={styles.emptyIcon}
          />
          <div css={styles.emptyText}>
            Drag folders here to build your project structure
          </div>
        </div>
      )}

      {isDraggedOver && <div css={styles.dropIndicator}>Drop folder here</div>}
    </div>
  );
};

interface ExistingFolderItemProps {
  folder: TableEntry;
  level: number;
}

const ExistingFolderItem: React.FC<ExistingFolderItemProps> = ({
  folder,
  level,
}) => {
  const styles = useStyles(styleFn);

  return (
    <div css={styles.itemWrapper}>
      <div
        css={[
          styles.item,
          styles.existingItem,
          { paddingLeft: level * 20 + 12 },
        ]}>
        <FontAwesomeIcon icon={['fas', 'folder']} css={styles.folderIcon} />
        <span css={styles.folderName}>{folder.title}</span>
        <Badge
          count="Existing"
          css={styles.existingBadge}
          style={{ backgroundColor: '#52c41a' }}
        />
      </div>
      {folder.children && folder.children.length > 0 && (
        <div css={styles.children}>
          {folder.children.map((child) => (
            <ExistingFolderItem
              key={child.key}
              folder={child}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface DroppableFolderItemProps {
  folder: TableEntry;
  level: number;
  onNest: (draggedId: number, targetId: number) => void;
  onRemove: (folderId: number) => void;
  canNest?: (draggedId: number, targetId: number) => boolean;
}

const DroppableFolderItem: React.FC<DroppableFolderItemProps> = ({
  folder,
  level,
  onNest,
  onRemove,
  canNest,
}) => {
  const styles = useStyles(styleFn);
  const ref = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return combine(
      draggable({
        element: el,
        getInitialData: () => ({ folder: folder.dbFolder, isNew: true }),
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => {
          const draggedFolder = source.data.folder as DbFolder;
          const isValid = draggedFolder.id !== folder.dbFolder.id;

          // Use custom validation if provided
          if (canNest && isValid) {
            return canNest(draggedFolder.id, folder.dbFolder.id);
          }

          return isValid;
        },
        onDragEnter: () => setIsDraggedOver(true),
        onDragLeave: () => setIsDraggedOver(false),
        onDrop: ({ source }) => {
          setIsDraggedOver(false);
          const draggedFolder = source.data.folder as DbFolder;
          const isNewFolder = source.data.isNew as boolean;
          if (isNewFolder && draggedFolder.id !== folder.dbFolder.id) {
            onNest(draggedFolder.id, folder.dbFolder.id);
          }
        },
      })
    );
  }, [folder, onNest, canNest]);

  return (
    <div css={styles.itemWrapper}>
      <div
        ref={ref}
        css={[
          styles.item,
          styles.newItem,
          isDraggedOver && styles.itemDraggedOver,
          { paddingLeft: level * 20 + 12 },
        ]}>
        <FontAwesomeIcon icon={['fas', 'folder']} css={styles.folderIcon} />
        <span css={styles.folderName}>{folder.title}</span>
        <FontAwesomeIcon
          icon={['fas', 'grip-vertical']}
          css={styles.dragIcon}
        />
        <Button
          type="text"
          danger
          size="small"
          onClick={() => onRemove(folder.dbFolder.id)}
          css={styles.removeButton}>
          <FontAwesomeIcon icon={['fas', 'times']} />
        </Button>
      </div>
      {folder.children && folder.children.length > 0 && (
        <div css={styles.children}>
          {folder.children.map((child) => (
            <DroppableFolderItem
              key={child.key}
              folder={child}
              level={level + 1}
              onNest={onNest}
              onRemove={onRemove}
              canNest={canNest}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    minHeight: 300,
    padding: 16,
    border: `2px dashed ${theme.colors.border}`,
    borderRadius: 8,
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  containerDraggedOver: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primaryLight,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 16,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  itemWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px',
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 6,
    transition: 'all 0.2s ease',
    userSelect: 'none',
  },
  existingItem: {
    opacity: 0.6,
    cursor: 'not-allowed',
    backgroundColor: theme.colors.backgroundDisabled,
    '&:hover': {
      backgroundColor: theme.colors.backgroundDisabled,
    },
  },
  newItem: {
    cursor: 'grab',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
      transform: 'translateX(2px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    '&:active': {
      cursor: 'grabbing',
      transform: 'scale(1.02)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  },
  itemDraggedOver: {
    borderColor: theme.colors.success,
    backgroundColor: '#f6ffed',
    boxShadow: '0 0 0 2px rgba(82, 196, 26, 0.2)',
  },
  folderIcon: {
    fontSize: 16,
    color: theme.colors.warning,
    flexShrink: 0,
  },
  folderName: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
  },
  dragIcon: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    opacity: 0.6,
  },
  removeButton: {
    padding: '4px 8px',
    height: 'auto',
    minWidth: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'rgba(255, 77, 79, 0.1)',
    },
  },
  existingBadge: {
    fontSize: 12,
    padding: '0 8px',
    height: 20,
    lineHeight: '20px',
  },
  children: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 48,
    color: theme.colors.textSecondary,
    opacity: 0.5,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  dropIndicator: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '12px 24px',
    backgroundColor: theme.colors.primary,
    color: '#fff',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    pointerEvents: 'none',
  },
});
